import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';

/**
 * Widget affichant les compteurs CP/RTT de l'utilisateur connecte
 * A integrer dans le tableau de bord ou le profil utilisateur
 */
const LeaveBalanceWidget = ({ token, compact = false }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const loadBalance = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/leave-balances/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setBalance(data.data);
      } else {
        setError('Erreur chargement compteurs');
      }
    } catch (err) {
      setError('Erreur connexion serveur');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
          Chargement...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ textAlign: 'center', color: '#dc3545', fontSize: '14px' }}>
          {error}
        </div>
      </div>
    );
  }

  if (!balance || balance.not_initialized) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>📊</span>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Mes conges</span>
        </div>
        <div style={{
          background: '#fff3cd',
          color: '#856404',
          padding: '12px',
          borderRadius: '8px',
          fontSize: '13px',
          textAlign: 'center'
        }}>
          Compteurs non initialises. Contactez les RH.
        </div>
      </div>
    );
  }

  // Version compacte (pour sidebar ou header)
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        gap: '16px',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: '#e8f5e9',
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '13px'
        }}>
          <span>CP</span>
          <strong style={{ color: '#2e7d32' }}>{balance.cp_balance || 0}j</strong>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: '#e3f2fd',
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '13px'
        }}>
          <span>RTT</span>
          <strong style={{ color: '#1565c0' }}>{balance.rtt_balance || 0}j</strong>
        </div>
      </div>
    );
  }

  // Version complete (pour dashboard)
  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          background: expanded ? '#f8f9fa' : 'white',
          transition: 'background 0.2s'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '22px' }}>📊</span>
          <span style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
            Mes conges
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Quick summary */}
          <div style={{
            display: 'flex',
            gap: '8px'
          }}>
            <span style={{
              background: '#e8f5e9',
              color: '#2e7d32',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              CP: {balance.cp_balance || 0}j
            </span>
            <span style={{
              background: '#e3f2fd',
              color: '#1565c0',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              RTT: {balance.rtt_balance || 0}j
            </span>
          </div>
          <span style={{
            fontSize: '12px',
            color: '#666',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s'
          }}>
            ▼
          </span>
        </div>
      </div>

      {/* Details (expandable) */}
      {expanded && (
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* CP Card */}
            <div style={{
              background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
              padding: '16px',
              borderRadius: '12px'
            }}>
              <h4 style={{ margin: '0 0 12px', color: '#2e7d32', fontSize: '14px' }}>
                Conges Payes
              </h4>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#1b5e20', marginBottom: '8px' }}>
                {balance.cp_balance || 0}
                <span style={{ fontSize: '16px', fontWeight: 'normal', marginLeft: '4px' }}>jours</span>
              </div>
              <div style={{ fontSize: '12px', color: '#558b2f' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Acquis</span>
                  <span>{balance.cp_total_acquired || 0}j</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Pris</span>
                  <span>-{balance.cp_taken || 0}j</span>
                </div>
                {(balance.cp_planned || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Planifies</span>
                    <span>-{balance.cp_planned}j</span>
                  </div>
                )}
                {(balance.cp_carried_remaining || 0) > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    paddingTop: '4px',
                    marginTop: '4px'
                  }}>
                    <span>Report N-1</span>
                    <span>+{balance.cp_carried_remaining}j</span>
                  </div>
                )}
              </div>
            </div>

            {/* RTT Card */}
            <div style={{
              background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
              padding: '16px',
              borderRadius: '12px'
            }}>
              <h4 style={{ margin: '0 0 12px', color: '#1565c0', fontSize: '14px' }}>
                RTT
              </h4>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#0d47a1', marginBottom: '8px' }}>
                {balance.rtt_balance || 0}
                <span style={{ fontSize: '16px', fontWeight: 'normal', marginLeft: '4px' }}>jours</span>
              </div>
              <div style={{ fontSize: '12px', color: '#1976d2' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Quota annuel</span>
                  <span>{balance.rtt_annual_quota || 0}j</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Pris</span>
                  <span>-{balance.rtt_taken || 0}j</span>
                </div>
                {(balance.rtt_planned || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Planifies</span>
                    <span>-{balance.rtt_planned}j</span>
                  </div>
                )}
                {(balance.rtt_carried_remaining || 0) > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    paddingTop: '4px',
                    marginTop: '4px'
                  }}>
                    <span>Report N-1</span>
                    <span>+{balance.rtt_carried_remaining}j</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info politique */}
          {balance.policy_name && (
            <div style={{
              marginTop: '12px',
              padding: '10px 12px',
              background: '#f8f9fa',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#666',
              textAlign: 'center'
            }}>
              Politique: <strong>{balance.policy_name}</strong>
              {balance.work_regime && ` (${balance.work_regime})`}
            </div>
          )}

          {/* Annee de reference */}
          <div style={{
            marginTop: '8px',
            fontSize: '11px',
            color: '#999',
            textAlign: 'center'
          }}>
            Periode de reference: {balance.reference_year}/{balance.reference_year + 1}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveBalanceWidget;
